import { Component, useEffect, useMemo, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router';
import * as CONSTANT from 'src/constants';
import { awsAssetsFetcher } from 'src/utils/awsAssetsFetcher';
import Radar from 'radar-sdk-js';

import styleClasses from '../location.module.scss';
import LocationMarker from '../LocationMarker';

const MapWithAMarker = (props) => {
  const routerLocation = useLocation();
  const { id: locationName } = useParams();
  let id = decodeURIComponent(locationName);

  const mapContainerRef = useRef(null);
  const radarMapRef = useRef(null);
  const popupRef = useRef(null);
  const radarMarkersRef = useRef(new Map()); // id -> Radar marker

  const markers = useMemo(() => {
    const singleMarker =
      routerLocation.pathname.includes('locationcopy');
    if (!singleMarker) return props.markers || [];
    return (props.markers || []).filter((item) => item.name == id);
  }, [routerLocation.pathname, props.markers, id]);

  // Initialize Radar map (once)
  useEffect(() => {
    if (!radarMapRef.current && mapContainerRef.current) {
      try {
        if (CONSTANT.RADAR_PUBLISHABLE_KEY) {
          Radar.initialize(CONSTANT.RADAR_PUBLISHABLE_KEY);
        }
      } catch (_) {}

      const defaultCenter = props.markers?.[0]
        ? [props.markers[0].longitude, props.markers[0].latitude]
        : [-97.476975969198, 38.51675742862462];

      radarMapRef.current = Radar.ui.map({
        container: mapContainerRef.current,
        style: 'radar-default-v1',
        center: defaultCenter,
        zoom: props.markers?.[0] ? 8 : 4,
        language: 'local',
      });

      radarMapRef.current.on('load', () => {
        drawMarkers();
        if ((props?.markers || []).length > 1) {
          try {
            radarMapRef.current.fitToMarkers();
          } catch (_) {}
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildPopupElement = (marker) => {
    const container = document.createElement('div');
    container.className = 'popup_container';

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.innerText = '×';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.style.position = 'absolute';
    closeBtn.style.right = '8px';
    closeBtn.style.top = '8px';
    closeBtn.style.background = 'transparent';
    closeBtn.style.border = 'none';
    closeBtn.style.cursor = 'pointer';
    closeBtn.addEventListener('click', () => {
      if (popupRef.current) {
        try {
          popupRef.current.remove();
        } catch (_) {}
        popupRef.current = null;
      }
      if (typeof props.onCloseClick === 'function') {
        props.onCloseClick(marker);
      }
    });
    container.appendChild(closeBtn);

    const wrap = document.createElement('div');
    wrap.className = `${styleClasses.map_popup_description_container} text-start`;

    const nameDiv = document.createElement('div');
    nameDiv.className = styleClasses.location_name;
    nameDiv.textContent = marker?.data?.name || '';
    wrap.appendChild(nameDiv);

    const addrDiv = document.createElement('div');
    addrDiv.className = `${styleClasses.location_address} ${styleClasses.location_address_popup}`;
    addrDiv.textContent = marker?.data?.address || '';
    wrap.appendChild(addrDiv);

    const phoneDiv = document.createElement('div');
    phoneDiv.className = styleClasses.location_phone_number;
    phoneDiv.textContent = marker?.data?.phone || '';
    wrap.appendChild(phoneDiv);

    if (marker?.data?.mon_thurs_timings) {
      const box = document.createElement('div');
      box.className = `${styleClasses.timming_box} ${styleClasses.timing_box_popup}`;
      const l1 = document.createElement('div');
      l1.className = styleClasses.location_phone_number;
      l1.textContent = 'Mon - Thu';
      const l2 = document.createElement('div');
      l2.className = styleClasses.location_phone_number;
      l2.textContent = marker.data.mon_thurs_timings;
      box.appendChild(l1);
      box.appendChild(l2);
      wrap.appendChild(box);
    }
    if (marker?.data?.fri_sat_timings) {
      const box = document.createElement('div');
      box.className = `${styleClasses.timming_box} ${styleClasses.timing_box_popup}`;
      const l1 = document.createElement('div');
      l1.className = styleClasses.location_phone_number;
      l1.textContent = 'Fri - Sat';
      const l2 = document.createElement('div');
      l2.className = styleClasses.location_phone_number;
      l2.textContent = marker.data.fri_sat_timings;
      box.appendChild(l1);
      box.appendChild(l2);
      wrap.appendChild(box);
    }
    const flex = document.createElement('div');
    flex.className = 'd-flex align-items-end';
    if (marker?.data?.sunday_timings) {
      const box = document.createElement('div');
      box.className = `${styleClasses.timming_box} ${styleClasses.timing_box_popup}`;
      const l1 = document.createElement('div');
      l1.className = styleClasses.location_phone_number;
      l1.textContent = 'Sunday';
      const l2 = document.createElement('div');
      l2.className = styleClasses.location_phone_number;
      l2.textContent = marker.data.sunday_timings;
      box.appendChild(l1);
      box.appendChild(l2);
      flex.appendChild(box);
    }
    const orderWrap = document.createElement('div');
    orderWrap.className = `${styleClasses.order_btn_wrap} text-end flex-fill`;
    const orderLink = document.createElement('a');
    const orderUrl =
      marker.order_type == 'small_order'
        ? marker?.data?.olo_individual_order_url
        : marker?.zipParamsCode
          ? marker?.data?.olo_take_out_order_url
          : marker?.data?.olo_individual_order_url;
    if (orderUrl) {
      orderLink.href = orderUrl;
      orderLink.className = `${styleClasses.order_button} menu-section`;
      orderLink.textContent = 'Order Now';
      orderWrap.appendChild(orderLink);
    }
    flex.appendChild(orderWrap);
    wrap.appendChild(flex);

    container.appendChild(wrap);
    return container;
  };

  const drawMarkers = () => {
    const map = radarMapRef.current;
    if (!map) return;

    const registry = radarMarkersRef.current;
    // use composite key to avoid collisions when ids repeat
    const makeKey = (m) => `${m.id}_${m.latitude}_${m.longitude}`;
    const nextIds = new Set((markers || []).map(makeKey));

    // remove stale markers
    for (const [key, mk] of registry.entries()) {
      if (!nextIds.has(key)) {
        try {
          mk.remove();
        } catch (_) {}
        registry.delete(key);
      }
    }

    // add/update markers
    (markers || []).forEach((marker) => {
      const key = makeKey(marker);
      let mk = registry.get(key);
      if (!mk) {
        const el = document.createElement('img');
        el.src = awsAssetsFetcher('Locations/Location_markup_green', 'svg');
        el.style.width = '25px';
        el.style.height = '30px';
        el.style.transform = 'translate(-50%, -100%)';
        el.style.cursor = 'pointer';

        // event parity with Google version
        el.addEventListener('mouseover', () => {
          if (typeof props.onClick === 'function') props.onClick(marker);
        });
        el.addEventListener('mouseout', (evt) => {
          if (typeof props.onMouseOut === 'function')
            props.onMouseOut(evt, marker);
        });
        el.addEventListener('click', () => {
          if (typeof props.onLocationClick === 'function')
            props.onLocationClick(marker);
        });

        mk = Radar.ui
          .marker({ element: el })
          .setLngLat([marker.longitude, marker.latitude])
          .addTo(map);
        registry.set(key, mk);
      } else {
        try {
          mk.setLngLat([marker.longitude, marker.latitude]);
        } catch (_) {}
      }

      // popup handling
      if (props.selectedMarker == marker && props.showMapPopUp == true) {
        try {
          if (popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
          }
          const element = buildPopupElement(marker);
          popupRef.current = Radar.ui
            .popup({ element })
            .setLngLat([marker.longitude, marker.latitude])
            .addTo(map);
        } catch (_) {}
      }
    });

    if (!(props.showMapPopUp && props.selectedMarker)) {
      if (popupRef.current) {
        try {
          popupRef.current.remove();
        } catch (_) {}
        popupRef.current = null;
      }
    }
  };

  // update markers and view on prop changes without recreating map
  useEffect(() => {
    drawMarkers();
    if (radarMapRef.current && (markers || []).length > 1) {
      try {
        radarMapRef.current.fitToMarkers();
      } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, props.selectedMarker, props.showMapPopUp]);

  return (
    <div style={{ height: '100%', width: '100%' }} ref={mapContainerRef} />
  );
};

export default class ShelterMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      shelters: props.lat_longs,
      selectedMarker: false,
      hoveredMarker: false,
    };
  }

  componentDidMount() {
    // fetch("https://api.harveyneeds.org/api/v1/shelters?limit=20")
    //   .then(r => r.json())
    //   .then(data => {
    //     this.setState({ shelters: data.shelters })
    //   })
  }
  handleClick = (marker) => {
    if (location.pathname.includes('locationcopy')) {
      return;
    }
    this.props.setShowInfo(false);
    this.props.openedMarker(marker);
    this.setState({ selectedMarker: marker });
    this.props.setShowMapPopUp(true);
  };

  handleLocationClick = (marker) => {
    if (location.pathname.includes('locationcopy')) {
      return;
    }
    if (this.props?.markerNotClickAble) return;
    this.props.openedMarker(marker);
    this.setState({ selectedMarker: marker });
    this.getInfo(marker.data.id, marker.data.name);
  };
  handleMouseOver = (marker) => {
    this.setState({ hoveredMarker: marker });
    this.props.openedMarker(marker);
  };

  getInfo = (id, name) => {
    this.props.navigate(`${CONSTANT.ROUTE_LOCATION_COPY}/info/${name}`);
    this.props.setShowInfo(true);
  };

  handleClosePopup = (marker, event) => {
    this.setState({ selectedMarker: false });
  };
  handleMouseOverOut = (event, marker) => {
    this.props.onMouseOut(event, marker);
    this.setState({ hoveredMarker: false });
  };
  render() {
    return (
      <MapWithAMarker
        selectedMarker={this.state.selectedMarker}
        hoveredMarker={this.state.hoveredMarker}
        showMapPopUp={this.props.showMapPopUp}
        markers={this.state.shelters}
        onLocationClick={this.handleLocationClick}
        onClick={this.handleClick}
        onMouseOver={this.handleMouseOver}
        onCloseClick={this.handleClosePopup}
        onMouseOut={this.handleMouseOverOut}
      />
    );
  }
}
